import { client } from './amplifyClient';

type UserModel = {
  id: string;
  email: string;
  name?: string;
  preferredUsername?: string;
  profilePicture?: string;
};

export interface OnboardingDataInput {
  userId: string;
  displayName: string;
  location: {
    name: string;
    latitude: number;
    longitude: number;
  } | null;
  preferences: {
    temperatureUnit: 'celsius' | 'fahrenheit';
    timeFormat: '12h' | '24h';
  };
  notificationsEnabled: boolean;
}

export async function upsertUser(input: UserModel) {
  const now = new Date().toISOString();

  try {
    // Try to create first - this handles new users
    const { errors: createErrors } = await client.models.User.create({
      id: input.id,
      email: input.email,
      name: input.name,
      preferredUsername: input.preferredUsername,
      profilePicture: input.profilePicture,
      notificationsEnabled: false,
      lastSignedIn: now,
    });

    // If create succeeded, we're done
    if (!createErrors || createErrors.length === 0) {
      return;
    }

    // If create failed due to existing record, try update
    const isConflict = createErrors.some(
      (e) => e.message?.includes('conditional') || e.message?.includes('already exists')
    );

    if (isConflict) {
      const { errors: updateErrors } = await client.models.User.update({
        id: input.id,
        email: input.email,
        name: input.name,
        preferredUsername: input.preferredUsername,
        profilePicture: input.profilePicture,
        lastSignedIn: now,
      });

      if (updateErrors && updateErrors.length > 0) {
        // Update failed - silent (user can still use the app)
      }
      return;
    }
  } catch (error) {
    // Silently handle errors - user sync is not critical for app functionality
  }
}

/**
 * Save onboarding data to backend.
 * Updates user profile, creates primary location, and sets preferences.
 */
export async function saveOnboardingData(input: OnboardingDataInput): Promise<void> {
  const { userId, displayName, location, preferences, notificationsEnabled } = input;

  // 1. Update user profile with display name
  const { errors: userErrors } = await client.models.User.update({
    id: userId,
    name: displayName,
    notificationsEnabled,
    onboardingComplete: true,
  });

  if (userErrors && userErrors.length > 0) {
    throw new Error(`Failed to update user profile: ${userErrors[0].message}`);
  }

  // 2. Save primary location if provided
  if (location) {
    // First, unset any existing primary location
    const { data: existingLocations } = await client.models.Location.list({
      filter: {
        userId: { eq: userId },
        isPrimary: { eq: true },
      },
    });

    if (existingLocations && existingLocations.length > 0) {
      for (const loc of existingLocations) {
        await client.models.Location.update({
          id: loc.id,
          isPrimary: false,
        });
      }
    }

    // Create new primary location
    const { errors: locationErrors } = await client.models.Location.create({
      userId,
      name: location.name,
      latitude: location.latitude,
      longitude: location.longitude,
      isPrimary: true,
    });

    if (locationErrors && locationErrors.length > 0) {
      throw new Error(`Failed to save location: ${locationErrors[0].message}`);
    }
  }

  // 3. Update or create user preferences
  const { data: existingPrefs } = await client.models.UserPreferences.list({
    filter: { userId: { eq: userId } },
  });

  const temperatureUnit = preferences.temperatureUnit === 'celsius' ? 'CELSIUS' : 'FAHRENHEIT';
  const timeFormat = preferences.timeFormat === '24h' ? 'TWENTY_FOUR_HOUR' : 'TWELVE_HOUR';

  if (existingPrefs && existingPrefs.length > 0) {
    const { errors: prefErrors } = await client.models.UserPreferences.update({
      id: existingPrefs[0].id,
      temperatureUnit,
      timeFormat,
    });

    if (prefErrors && prefErrors.length > 0) {
      throw new Error(`Failed to update preferences: ${prefErrors[0].message}`);
    }
  } else {
    const { errors: prefErrors } = await client.models.UserPreferences.create({
      userId,
      temperatureUnit,
      timeFormat,
      windSpeedUnit: 'MPH',
      theme: 'AUTO',
    });

    if (prefErrors && prefErrors.length > 0) {
      throw new Error(`Failed to create preferences: ${prefErrors[0].message}`);
    }
  }
}
